import { Component, OnInit, OnDestroy } from '@angular/core'; // Added OnDestroy
import { ActivatedRoute } from '@angular/router';
import { CommonModule, NgFor, NgIf } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { OtherProductService } from '../../../../Services/OtherProduct/other-product.service';
import { NotificationService } from '../../../../Services/notification.service';
import { CategoryService } from '../../../../Services/Category/category.service';
import { HttpParams } from '@angular/common/http';
import { FirmSelectionService } from '../../../../Services/firm-selection.service';
import { Subscription } from 'rxjs'; // Added Subscription

@Component({
  selector: 'app-non-jewellery-product',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, NgFor],
  templateUrl: './non-jewellery-product.component.html',
  styleUrl: './non-jewellery-product.component.css'
})
export class NonJewelleryProductComponent implements OnInit, OnDestroy { // Implemented OnDestroy
  productForm!: FormGroup;
  otherProductsList: any[] = [];
  addbutton: boolean = true;
  selectedId: any = null;
  isSubmitting: boolean = false;
  categories: any[] = [];
  firmId: any;
  private firmSubscription: Subscription = new Subscription(); // Added subscription

  constructor(
    private fb: FormBuilder,
    private otherProductService: OtherProductService,
    private notify: NotificationService,
    private route: ActivatedRoute,
    private categoryService: CategoryService,
    private firmSelectionService: FirmSelectionService
  ) { }

  ngOnInit() {
    this.initForm();

    // Listen for firm changes (Filtering Logic)
    this.firmSubscription = this.firmSelectionService.selectedFirmName$.subscribe((firm) => {
      if (firm) {
        this.firmId = firm.id;
        this.productForm.patchValue({ firm_id: this.firmId });
        this.loadAllData(); // Refresh data for the specific firm
      }
    });
  }

  // Refactored data loading to be firm-aware
  loadAllData() {
    // 1. Load categories first
    let params = new HttpParams().set('type', 'Product').set('firm_id', this.firmId);
    this.categoryService.getCategories(params).subscribe((categories: any) => {
      this.categories = categories.data;

      // 2. Now load products (categories are ready for enrichment)
      this.loadProducts();

      // 3. Check if we need to edit a product
      this.route.params.subscribe(params => {
        const id = params['id'];
        if (id) {
          this.selectedId = id;
          this.loadProductForEdit(id);
        } else {
          this.fetchNextCode();
        }
      });
    });
  }

  initForm() {
    this.productForm = this.fb.group({
      product_code: [''],
      category_code: [''],
      name: ['', Validators.required],
      cost_price: ['', [Validators.required, Validators.min(0)]],
      selling_price: ['', [Validators.required, Validators.min(0)]],
      quantity: ['', [Validators.required, Validators.min(0)]],
      category_id: [''],
      firm_id: [this.firmId],
    });
  }

  // Modified to use firm_id
  getCategories() {
    let params = new HttpParams().set('type', 'Product').set('firm_id', this.firmId);
    this.categoryService.getCategories(params).subscribe((categories: any) => {
      this.categories = categories.data;
    });
  }

  loadProductForEdit(id: any) {
    this.otherProductService.getOtherProduct(id).subscribe({
      next: (res: any) => {
        if (res.success) {
          this.patchProduct(res.data);
        } else {
          this.notify.showError("Product not found", "Error");
          this.fetchNextCode();
        }
      },
      error: () => {
        this.notify.showError("Error fetching product details", "Error");
        this.fetchNextCode();
      }
    });
  }

  // Modified to use firm_id for code generation
  fetchNextCode() {
    this.otherProductService.getNextProductCode().subscribe((res: any) => {
      if (res.success) {
        this.productForm.patchValue({ product_code: res.data.product_code });
      }
    });
  }

  // Modified to use firm_id for list filtering
  loadProducts() {
    let params = new HttpParams().set('firm_id', this.firmId);
    this.otherProductService.getOtherProducts(params).subscribe((res: any) => {
      if (res.success) {
        this.otherProductsList = [...res.data].reverse().slice(0, 10).map((item: any) => {
          const cat = this.categories?.find((c: any) => c.id == item.category_id);
          return { ...item, category: item.category ?? cat };
        });
      }
    });
  }

  onSubmit() {
    if (this.productForm.invalid) {
      this.notify.showWarning("Please fill all required fields", "Validation");
      return;
    }

    this.isSubmitting = true;
    this.otherProductService.createOtherProduct(this.productForm.value).subscribe({
      next: (res: any) => {
        if (res.success) {
          this.notify.showSuccess(res.message, "Success");
          this.clearForm();
          this.loadProducts();
        }
        this.isSubmitting = false;
      },
      error: (err) => {
        this.notify.showError("Failed to create product", "Error");
        this.isSubmitting = false;
      }
    });
  }

  patchProduct(item: any) {
    this.addbutton = false;
    this.selectedId = item.id;

    const catObj = (item.category && typeof item.category === 'object')
      ? item.category
      : this.categories.find((c: any) => c.id == item.category_id);
    const catId = catObj ? catObj.id : item.category_id;
    const catCode = catObj ? (catObj.code ?? '') : '';

    this.productForm.patchValue({
      product_code: item.product_code,
      name: item.name,
      cost_price: item.cost_price,
      selling_price: item.selling_price,
      quantity: item.quantity,
      category_id: catId,
      category_code: catCode,
      firm_id: item.firm_id
    });
  }

  updateForm() {
    if (this.productForm.invalid) {
      this.notify.showWarning("Please fill all required fields", "Validation");
      return;
    }

    this.isSubmitting = true;
    this.otherProductService.updateOtherProduct(this.selectedId, this.productForm.value).subscribe({
      next: (res: any) => {
        if (res.success) {
          this.notify.showSuccess(res.message, "Updated");
          this.clearForm();
          this.loadProducts();
        }
        this.isSubmitting = false;
      },
      error: () => {
        this.notify.showError("Update failed", "Error");
        this.isSubmitting = false;
      }
    });
  }

  onDelete(id: any) {
    if (confirm("Are you sure you want to delete this product?")) {
      this.otherProductService.deleteOtherProduct(id).subscribe((res: any) => {
        if (res.success) {
          this.notify.showSuccess(res.message, "Deleted");
          this.loadProducts();
          if (this.selectedId === id) this.clearForm();
        }
      });
    }
  }

  clearForm() {
    this.productForm.reset({
      firm_id: this.firmId,
      category_id: '',
      category_code: '',
    });
    this.addbutton = true;
    this.selectedId = null;
    this.fetchNextCode();
  }

  onCategoryChanged(event: any) {
    const selectedId = event.target.value;
    const selectedCategory = this.categories.find(
      (cat: any) => cat.id == selectedId
    );
    if (selectedCategory) {
      this.productForm.get('category_code')?.patchValue(selectedCategory.code ?? '');
    }
  }

  focusNext(event: Event) {
    const keyboardEvent = event as KeyboardEvent;
    if (keyboardEvent.key !== 'Enter') return;
    keyboardEvent.preventDefault();
    const target = keyboardEvent.target as HTMLElement;
    const form = target.closest('form');
    if (!form) return;
    const focusableElements = Array.from(form.querySelectorAll<HTMLElement>('input, select, textarea, button'))
      .filter(el => !el.hasAttribute('disabled') && !el.hasAttribute('readonly') && el.tabIndex !== -1 && el.offsetParent !== null);
    const currentIndex = focusableElements.indexOf(target);
    if (currentIndex !== -1 && currentIndex + 1 < focusableElements.length) {
      focusableElements[currentIndex + 1].focus();
    }
  }

  ngOnDestroy() {
    if (this.firmSubscription) {
      this.firmSubscription.unsubscribe();
    }
  }
}